import AutomaticReply from '@/shared/workReports/AutomaticReply';
import { reportsApi } from '@/shared/workReports/ReportsPage';
import { useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import type { ComponentType, JSX, LazyExoticComponent } from 'react';
import type { AppRole } from '@/shared/roles/role';
import type { BootstrapWorkflowSettings } from '@/app/bootstrap/types';
import { Loader } from '@/shared/ui/Loader';
import { RuntimeStateBoundary } from '@/shared/ui/RuntimeStateBoundary';

type RoleRouterProps = {
  role: AppRole;
  workflowSettings: BootstrapWorkflowSettings;
};

export type RoleRoutesProps = {
  workflowSettings: BootstrapWorkflowSettings;
};

type RoleRoutesComponent = ComponentType<RoleRoutesProps>;

const ClientRoutes = lazy(async () => {
  const module = await import('@/roles/client/ClientRoutes');
  return { default: module.ClientRoutes as RoleRoutesComponent };
});

const ManagerRoutes = lazy(async () => {
  const module = await import('@/roles/manager/ManagerRoutes');
  return { default: module.ManagerRoutes as RoleRoutesComponent };
});

const SpecialistRoutes = lazy(async () => {
  const module = await import('@/roles/specialist/SpecialistRoutes');
  return { default: module.SpecialistRoutes as RoleRoutesComponent };
});

const ROUTER_BY_ROLE: Record<AppRole, LazyExoticComponent<RoleRoutesComponent>> = {
  client: ClientRoutes,
  manager: ManagerRoutes,
  specialist: SpecialistRoutes,
};

export function RoleRouter({ role, workflowSettings }: RoleRouterProps): JSX.Element {
  const location = useLocation(); const navigate = useNavigate();
  const requestId = new URLSearchParams(location.search).get('automation_request');
  if (requestId) return <AutomaticReply api={reportsApi} requestId={requestId} onBack={() => {
    const params = new URLSearchParams(location.search); params.delete('automation_request'); navigate({ pathname: role === 'client' ? '/' : `/${role}`, search: params.toString() });
  }} />;
  const RoutesByRole = ROUTER_BY_ROLE[role];
  return (
    <RuntimeStateBoundary>
      <Suspense fallback={<Loader isHeartbeat />}>
        <RoutesByRole workflowSettings={workflowSettings} />
      </Suspense>
    </RuntimeStateBoundary>
  );
}
